import Phaser from 'phaser';
import {computeOutlinePixels} from './computeOutlinePixels';
import {cropPixels, findContentBounds} from './trimTextureFrame';

/**
 * Текстура сплошной обводки по силуэту существующей текстуры (контуры отрядов
 * и флагов на поле, см. HexGridRenderer). Возвращает ключ готовой текстуры или
 * null, если источник пока не прочитался — тогда обводку на этой перерисовке
 * просто не рисуют, а следующая попробует снова.
 *
 * ПОЧЕМУ ПИКСЕЛИ В ПАМЯТИ, А НЕ В CANVAS. Раньше обводка регистрировалась
 * через textures.addCanvas, и на мобильных после возврата из фона черные
 * контуры отрядов иногда пропадали. Механизм: в фоне браузер теряет
 * GPU-контекст. Phaser 3.90 на webglcontextrestored заново заливает КАЖДУЮ
 * текстуру из ее источника (WebGLRenderer.dispatchContextRestored ->
 * WebGLTextureWrapper.createResource), и у canvas-текстуры источник — сам
 * DOM-canvas. Сам элемент потерю переживает, а его содержимое — нет: 2D-канвас
 * без getImageData-чтений Chromium держит на GPU, и при потере GPU-контекста
 * очищает его в прозрачный (HTMLCanvasElement::NotifyGpuContextLost ->
 * CanvasRenderingContext2D::LoseContext; спецификация HTML: «Clear canvas's
 * bitmap to transparent black»). Phaser заливал пустоту, ключ оставался в
 * кэше, и обводка больше не появлялась до полной перезагрузки поля. Картинки
 * из файлов (сами отряды) при этом восстанавливались нормально.
 *
 * Текстура из Uint8Array восстанавливается из JS-массива
 * (WebGLTextureWrapper._processTexture, ветка Uint8Array), и никакой 2D-канвас
 * в этом не участвует. Удалять ключи на RESTORE_WEBGL по-прежнему нельзя:
 * текстуры держат живые спрайты, и исключение внутри отрисовки убивало
 * RAF-цикл Phaser насовсем (так было в 6a249f08, откачено в ca93d2e3).
 *
 * @param {Phaser.Scene} scene
 * @param {string} sourceKey - ключ исходной текстуры
 * @param {string} outlineKey - ключ текстуры обводки
 * @param {number} [thickness=2] - толщина обводки в пикселях текстуры обводки
 * @param {number} [color=0xFFFFFF] - цвет обводки
 * @param {Object} [options]
 * @param {number} [options.sourceScale=1] - во сколько раз уменьшить источник
 *   перед расчетом (0..1). Обводка считается по каждому пикселю источника, и у
 *   крупной картинки, которая на поле рисуется мелко (иконка монет 512 px при
 *   30 px на экране), полный расчет — лишние десятки миллисекунд. Текстура
 *   обводки выходит уменьшенной во столько же раз: ее спрайту нужен масштаб
 *   источника, деленный на sourceScale
 * @returns {string|null} outlineKey или null
 */
export function generateOutlineTexture(
  scene, sourceKey, outlineKey, thickness = 2, color = 0xFFFFFF, {sourceScale = 1} = {}
) {
  const textureManager = scene.textures;

  if (textureManager.exists(outlineKey)) {
    return outlineKey;
  }

  const sourceImage = textureManager.get(sourceKey)?.getSourceImage();
  const srcWidth = sourceImage ? (sourceImage.naturalWidth || sourceImage.width) : 0;
  const srcHeight = sourceImage ? (sourceImage.naturalHeight || sourceImage.height) : 0;
  if (!srcWidth || !srcHeight) {
    return null;
  }

  const isScaledDown = sourceScale > 0 && sourceScale < 1;
  const readWidth = isScaledDown ? Math.max(1, Math.round(srcWidth * sourceScale)) : srcWidth;
  const readHeight = isScaledDown ? Math.max(1, Math.round(srcHeight * sourceScale)) : srcHeight;

  // Канвас только для чтения пикселей источника. willReadFrequently держит его
  // на CPU: чтение с GPU-канваса, чье содержимое потеряно, вернуло бы нули
  const readCanvas = document.createElement('canvas');
  readCanvas.width = readWidth;
  readCanvas.height = readHeight;
  const readCtx = readCanvas.getContext('2d', {willReadFrequently: true});
  if (!readCtx) {
    return null;
  }
  if (isScaledDown) {
    // Уменьшенная копия — со сглаживанием: без него край силуэта пошел бы
    // ступеньками, и обводка повторила бы их
    readCtx.imageSmoothingEnabled = true;
    readCtx.drawImage(sourceImage, 0, 0, readWidth, readHeight);
  } else {
    readCtx.imageSmoothingEnabled = false;
    readCtx.drawImage(sourceImage, 0, 0);
  }
  const srcData = readCtx.getImageData(0, 0, readWidth, readHeight).data;

  const outline = computeOutlinePixels(srcData, readWidth, readHeight, thickness, color);
  // Пустой результат не кэшируем: в кэше пустая обводка осталась бы навсегда
  if (!outline) {
    return null;
  }

  // В текстуре только сама обводка: прозрачное поле занимало бы видеопамять, и GPU
  // закрашивал бы его зря. Спрайт ведет себя как кадр полного размера
  const bounds = findContentBounds(outline.data, outline.width, outline.height);
  const texture = textureManager.addUint8Array(
    outlineKey, cropPixels(outline.data, outline.width, bounds), bounds.width, bounds.height,
  );
  if (!texture) {
    return null;
  }
  texture.get?.().setTrim(outline.width, outline.height, bounds.x, bounds.y, bounds.width, bounds.height);
  // У текстуры из массива фильтрация по умолчанию NEAREST — контур на зуме
  // пошел бы ступеньками, а canvas-текстура раньше была сглаженной
  texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

  return outlineKey;
}
